import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTypingTest } from '../hooks/useTypingTest';
import { authHeaders, draftKey } from '../lib/tries';
import { browserId } from '../lib/browserId';

interface TypingTestProps {
  identifier: string;
  /** Which of the code's tries this is (each try has its own passage) */
  tryNumber: number;
  onComplete: (results: any) => void;
  isPractice: boolean;
  /** Instructions shown under the test until typing starts (the space is kept so nothing moves) */
  notice?: React.ReactNode;
}

const DURATION = 60;

const TypingTest: React.FC<TypingTestProps> = ({ identifier, tryNumber, onComplete, isPractice, notice }) => {
  const [story, setStory] = useState('');
  const [loading, setLoading] = useState(true);

  // Load story from server
  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(`/api/test/stories?identifier=${identifier}&try=${tryNumber}`);
        setStory(response.data.story);
        setLoading(false);
      } catch {
        setStory('error loading test passage please refresh the page and try again');
        setLoading(false);
      }
    };
    load();
  }, [identifier, tryNumber]);

  const computeConsistency = useCallback((timestamps: number[]): number => {
    if (timestamps.length < 3) return 100;
    const intervals: number[] = [];
    for (let i = 1; i < timestamps.length; i++) intervals.push(timestamps[i] - timestamps[i - 1]);
    const mean = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    if (mean === 0) return 100;
    const stddev = Math.sqrt(intervals.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / intervals.length);
    return Math.max(0, Math.min(100, Math.round((1 - stddev / mean) * 100)));
  }, []);

  const computeLocalMetrics = useCallback((input: string, txt: string, duration: number) => {
    const typedChars = input.length;
    const correctChars = input.split('').filter((c, i) => c === txt[i]).length;
    const incorrectChars = typedChars - correctChars;
    const totalWords = Math.max(1, Math.floor(typedChars / 5));
    const correctWords = Math.floor(correctChars / 5);
    const incorrectWords = totalWords - correctWords;
    const rawWPM = duration > 0 ? Math.min(500, Math.round((typedChars / 5) / (duration / 60))) : 0;
    const netWPM = duration > 0 ? Math.min(500, Math.max(0, Math.round((correctChars / 5) / (duration / 60)))) : 0;
    const accuracy = typedChars > 0 ? Math.round((correctChars / typedChars) * 100) : 0;
    const adjustedWPM = Math.min(500, Math.max(0, Math.round(rawWPM * (accuracy / 100))));
    const errorsPerMinute = parseFloat(Math.min(999.99, duration > 0 ? incorrectChars / (duration / 60) : 0).toFixed(2));
    return {
      wpm: netWPM, raw_wpm: rawWPM, accuracy, adjusted_wpm: adjustedWPM,
      consistency: 100, errors_per_minute: errorsPerMinute,
      total_words: totalWords, correct_words: correctWords, incorrect_words: incorrectWords,
    };
  }, []);

  const handleEnd = useCallback(async (input: string, actualDuration: number, timestamps: number[]) => {
    const consistency = computeConsistency(timestamps);

    if (!isPractice) {
      // Update draft with final typed state before attempting submission
      localStorage.setItem(draftKey(identifier), JSON.stringify({
        payload: {
          identifier_code: identifier,
          try_number: tryNumber,
          typed_text: input,
          original_text: story,
          test_duration: 60,
          consistency: null,
        },
        savedAt: Date.now(),
      }));

      const payload = {
        identifier_code: identifier,
        try_number: tryNumber,
        typed_text: input,
        original_text: story,
        test_duration: actualDuration,
        consistency,
        browser_id: browserId(),
      };

      const submitHeaders = authHeaders();

      let submitted = false;
      let serverMetrics: any = null;

      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const response = await axios.post('/api/test/submit', payload, { timeout: 10000, headers: submitHeaders });
          if (response.data.success) {
            submitted = true;
            serverMetrics = response.data.metrics;
            localStorage.removeItem(draftKey(identifier));
            break;
          }
        } catch (e: any) {
          // The server refused this try (e.g. no tries left); retrying won't change that
          if (e?.response?.status === 409) {
            localStorage.removeItem(draftKey(identifier));
            break;
          }
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
          } else {
            console.error('Failed to submit test results after 3 attempts:', e);
          }
        }
      }

      const metrics = serverMetrics ?? computeLocalMetrics(input, story, actualDuration);
      onComplete({
        identifier_code: identifier,
        try_number: tryNumber,
        ...metrics,
        test_duration: serverMetrics ? serverMetrics.test_duration : actualDuration,
        typed_text: input,
        original_text: story,
        _submitted: submitted,
      });
    } else {
      const metrics = computeLocalMetrics(input, story, actualDuration);
      onComplete({
        identifier_code: identifier,
        ...metrics,
        consistency,
        test_duration: actualDuration,
        typed_text: input,
        original_text: story,
      });
    }
  }, [identifier, tryNumber, isPractice, story, computeConsistency, computeLocalMetrics, onComplete]);

  const {
    userInput,
    testEnded,
    testStarted,
    hasInputError,
    isFocused,
    setIsFocused,
    lineTransition,
    textBlockWidth,
    displayWPM,
    currentWord,
    renderVisibleText,
    inputRef,
    containerRef,
    handleKeyDown,
    handleInputChange,
    handleContainerClick,
    formatTime,
    timeLeft,
  } = useTypingTest({ text: story, duration: DURATION, onEnd: handleEnd });

  // Keep a ref so the save interval always reads the latest input without re-creating
  const draftInputRef = useRef('');
  useEffect(() => { draftInputRef.current = userInput; }, [userInput]);

  // Save progress to localStorage periodically so a refresh or browser close can recover
  useEffect(() => {
    if (!testStarted || testEnded || !story || isPractice) return;
    const saveDraft = () => {
      localStorage.setItem(draftKey(identifier), JSON.stringify({
        payload: {
          identifier_code: identifier,
          try_number: tryNumber,
          typed_text: draftInputRef.current,
          original_text: story,
          test_duration: 60,
          consistency: null,
        },
        savedAt: Date.now(),
      }));
    };
    saveDraft(); // save immediately on first keypress
    const interval = setInterval(saveDraft, 5000);
    return () => clearInterval(interval);
  }, [testStarted, testEnded, identifier, tryNumber, story, isPractice]);

  if (loading) {
    return (
      <div className="typing-test">
        <div className="text-display" style={{ justifyContent: 'center' }}>
          <div className="loading" />
        </div>
        <div className="stats-bar">
          <div className="input-display" style={{ flex: 1, height: 48 }} />
          <div className="stat-box wpm-box" />
          <div className="stat-box timer-box" />
        </div>
      </div>
    );
  }

  return (
    <div className="typing-test">
      {/* Text Display */}
      <div
        ref={containerRef}
        onClick={handleContainerClick}
        className="text-display"
      >
        <div
          className={`text-lines-inner${lineTransition ? ' sliding' : ''}`}
          style={textBlockWidth > 0 ? { width: `${textBlockWidth}px` } : undefined}
        >
          {renderVisibleText}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div
          className={`input-display${isFocused ? ' focused' : ''}${hasInputError ? ' input-error' : ''}`}
          onClick={handleContainerClick}
        >
          <span className="input-text">{currentWord}</span>
          <span className={`input-cursor${testEnded || !isFocused ? ' hidden' : ''}`} />
        </div>

        <div className="stat-box wpm-box">
          <span className="stat-value">{displayWPM}</span>
          <span className="stat-label">WPM</span>
        </div>

        <div className="stat-box timer-box">
          <span className={`stat-value${timeLeft <= 5 && testStarted ? ' warning' : ''}`}>
            {formatTime(timeLeft)}
          </span>
        </div>
      </div>

      <div className="test-footer">
        {testEnded ? (
          <div className="test-status" role="status">
            <span className="loading" style={{ width: 16, height: 16, borderWidth: 2 }} />
            Test complete, saving your results…
          </div>
        ) : (
          notice && (
            <div className={`test-notice${testStarted ? ' hidden' : ''}`} aria-hidden={testStarted}>
              {notice}
            </div>
          )
        )}
      </div>

      {/* Hidden real input */}
      <form autoComplete="off" onSubmit={e => e.preventDefault()} className="hidden-input-form">
        <input
          ref={inputRef}
          type="text"
          onKeyDown={handleKeyDown}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={testEnded}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          autoFocus
          className="hidden-input"
        />
      </form>
    </div>
  );
};

export default TypingTest;
